export function ema(values, period, { seed = 'sma' } = {}) {
  const n = Math.max(1, Math.floor(Number(period) || 1));
  const out = new Array(values.length).fill(null);
  const k = 2 / (n + 1);
  let prev = null;
  if (seed === 'first') {
    for (let i = 0; i < values.length; i++) {
      const v = values[i];
      if (!Number.isFinite(v)) {
        out[i] = prev;
        continue;
      }
      prev = prev == null ? v : (v - prev) * k + prev;
      out[i] = prev;
    }
    return out;
  }
  let seedSum = 0;
  let seedCount = 0;
  for (let i = 0; i < values.length; i++) {
    const v = values[i];
    if (!Number.isFinite(v)) {
      out[i] = prev;
      continue;
    }
    if (prev == null) {
      seedSum += v;
      seedCount += 1;
      if (seedCount >= n) {
        prev = seedSum / seedCount;
        out[i] = prev;
      }
      continue;
    }
    prev = (v - prev) * k + prev;
    out[i] = prev;
  }
  return out;
}

export function emaFromBars(bars, period, field = 'close') {
  return ema((bars || []).map((b) => b?.[field]), period);
}

/**
 * EMA of daily closes (grouped by calendar date in `timeZone`), mapped onto every
 * intrabar as the latest completed daily EMA — matches Macro Bullet's daily bias.
 */
export function dailyEmaMapped(bars, period, timeZone = 'America/Los_Angeles') {
  const out = new Array((bars || []).length).fill(null);
  if (!bars?.length) return out;

  const byDay = new Map();
  for (let i = 0; i < bars.length; i++) {
    const key = zonedDateKey(bars[i].timestamp || bars[i].timeSec * 1000, timeZone);
    if (!key) continue;
    byDay.set(key, { close: bars[i].close, lastIndex: i });
  }

  const days = Array.from(byDay.entries()).sort((a, b) => (a[0] < b[0] ? -1 : 1));
  const dailyCloses = days.map(([, v]) => v.close);
  const dailyEma = ema(dailyCloses, period, { seed: 'first' });

  let cursor = 0;
  for (let i = 0; i < bars.length; i++) {
    const key = zonedDateKey(bars[i].timestamp || bars[i].timeSec * 1000, timeZone);
    while (cursor < days.length - 1 && days[cursor][0] < key) cursor += 1;
    // Use previous completed day when we're still in the current session.
    const emaIndex = days[cursor] && days[cursor][0] === key ? cursor - 1 : cursor;
    out[i] = emaIndex >= 0 ? dailyEma[emaIndex] : null;
  }
  return out;
}

export function zonedDateKey(input, timeZone = 'America/Los_Angeles') {
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return null;
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return fmt.format(d);
}
